import * as React from 'react';

/* 
  This component is used to dynamically render content 
  based on the value of a given input. You declare an 
  inputId in the props, then pass children with at least
  one input that contains that id. You can then pass it the
  DynamicItem component as a child with the condition prop.
  A DynamicItem will only render if the condition that you pass
  matches the current value of the input.

  This component is completely cms driven. Any type of input with
  a string value can be used, it can be placed anywhere within this 
  wrapper. Any component not wrapped in a DynamicItem will always
  render allowing the cms author to have complete control over the
  nature of how it will appear.
*/

interface DynamicFormProps {
  children: React.ReactElement | React.ReactElement[];
  inputId?: string;
}

export default function DynamicContent(props: DynamicFormProps) {
  const { inputId = 'dynamicValue' } = props;
  const [contentState, setContentState] = React.useState(props.children);
  const [inputValue, setInputValue] = React.useState<string | undefined>(
    undefined
  );

  let keyIndex = 0;

  const triggerChanges = (
    changeArr: { changeFunc: Function; value: any; valid?: boolean }[]
  ) => {
    changeArr.forEach(async (event) => {
      const e = new Event('change');
      let changeObj: Object = {
        ...e,
        value: event.value
      };
      if (event.valid !== undefined) {
        changeObj = {
          ...changeObj,
          valid: event.valid
        };
      }
      await setTimeout(() => {
        event.changeFunc(changeObj);
      }, 2);
    });
  };

  let hiddenChanges: { changeFunc: Function; value: any; valid: true }[] = [];

  let visibleChanges: { changeFunc: Function; value: any; valid: boolean }[] =
    [];

  const findDynamicElements = (el: any, condition = '') => {
    let elChildren: any[] = [];
    if (el.props?.children) {
      elChildren = Array.isArray(el.props.children)
        ? el.props.children
        : [el.props.children];
    }

    elChildren = elChildren.map((child) => {
      let newCondition = condition;
      if (child.props?.condition) {
        newCondition = child.props.condition;
      }
      const newChildren = findDynamicElements(child, newCondition);

      let newChild = child;

      if (newChildren.length > 0) {
        newChild = React.cloneElement(child, {
          ...child.props,
          key: keyIndex,
          children: newChildren
        });
      }

      if (child.props?.id === inputId) {
        newChild = React.cloneElement(newChild, {
          ...newChild.props,
          key: keyIndex,
          onChange: (e: any) => {
            if (child.props.onChange) {
              child.props.onChange(e);
            }
            let value;
            if (e.target?.type === 'checkbox' || e.target?.type === 'radio') {
              value = e.target.checked ? e.target.value : '';
            } else if (e.target?.value) {
              value = e.target.value;
            } else if (e.value) {
              value = e.value;
            }
            setInputValue(value);
          }
        });
        if (child.props?.value && inputValue === undefined) {
          let { value } = child.props;
          if (child.type?.name === 'CheckBox') {
            value = !!child.props.checked;
          }
          setInputValue(value);
        }
      }

      if (child.props) {
        newChild = React.cloneElement(newChild, {
          ...newChild.props,
          key: keyIndex,
          currentCondition: inputValue
        });
        if (
          newChild.props.name &&
          newChild.props.onChange &&
          newChild.props?.id !== inputId &&
          inputValue !== newCondition
        ) {
          hiddenChanges.push({
            value: undefined,
            valid: true,
            changeFunc: newChild.props.onChange
          });
        } else if (
          newChild.props.name &&
          newChild.props.onChange &&
          newChild.props?.id !== inputId &&
          inputValue === newCondition
        ) {
          let isValid = true;
          let { value } = newChild.props;
          if (child.props?.required) {
            isValid = !!child.props.value;
            if (child.type?.name === 'CheckBox') {
              isValid = !!child.props.checked;
            }
          }
          if (child.props?.value) {
            if (child.type?.name === 'CheckBox') {
              value = newChild.props.checked ? newChild.props.value : undefined;
            }
          }
          visibleChanges.push({
            value,
            changeFunc: newChild.props.onChange,
            valid: isValid
          });
        }
      }

      keyIndex += 1;

      return newChild;
    });
    return elChildren;
  };

  React.useEffect(() => {
    setContentState(findDynamicElements(<div>{contentState}</div>));
    if (hiddenChanges.length > 0) {
      triggerChanges(hiddenChanges);
    }
    if (visibleChanges.length > 0) {
      triggerChanges(visibleChanges);
    }
    hiddenChanges = [];
    visibleChanges = [];
  }, [inputValue]);

  return <div className="dynamic-content">{contentState}</div>;
}
